import { Box, Flex, FormControl, FormErrorMessage, Icon, IconButton, Td, Text, Tr } from "@chakra-ui/react";
import { DeleteIcon } from "@chakra-ui/icons";
// The same icon the sidebar gives the requirement library, so the action points somewhere recognisable.
import { FiBookOpen } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import type {
  AwardCriterionDto,
  AwardSubCriterionDto,
  SourcingEventDto,
  SourcingEventPatchDto,
} from "../../../../../autogen/bff-api";
import { CommentSubjectKind, WeightType } from "../../../../../autogen/bff-api";
import { ItemComments } from "./commentRound/ItemComments";
import { useEditor, useFieldErrorTranslation } from "../../../../../common/editing/save-coordinator";
import { rowOpenProps } from "../../../../../common/clickableRow";
import { effectiveWeight, formatWeightPercent, subCriterionStatus } from "../../../../../common/award-criteria/awardCriteriaStatus";
import { AwardStatusChip } from "./AwardStatusChip";
import { WeightInput } from "../../../../../common/award-criteria/WeightInput";

// t('Complete') t('Missing weight') t('Missing description') t('Delete sub-criterion') t('Weight')
// t('of the total') t('Save to the requirement library') t('Already in the requirement library')

interface Props {
  eventId: string;
  criterionIndex: number;
  subIndex: number;
  criterion: AwardCriterionDto;
  sub: AwardSubCriterionDto;
  isLocked: boolean;
  onChange: (next: AwardSubCriterionDto) => void;
  onOpen: () => void;
  onDelete: () => void;
  /** Keeps this requirement for reuse. Omitted when the library is off or the user may not write to it. */
  onSaveToLibrary?: () => void;
  /** The library already holds a requirement by this name, so there is nothing left to keep. */
  isInLibrary?: boolean;
  stacked?: boolean;
}

const STATUS_LABEL_KEY = {
  complete: "Complete",
  "missing-weight": "Missing weight",
  "missing-description": "Missing description",
} as const;

export const AwardSubCriterionRow = ({
  eventId,
  criterionIndex,
  subIndex,
  criterion,
  sub,
  isLocked,
  onChange,
  onOpen,
  onDelete,
  onSaveToLibrary,
  isInLibrary = false,
  stacked = false,
}: Props) => {
  const { t } = useTranslation();
  const formatFieldError = useFieldErrorTranslation();
  const { fieldError } = useEditor<SourcingEventDto, SourcingEventPatchDto>(eventId);
  const weightFieldError = fieldError(
    `eventData.awardCriteria.bestPriceQualityRatio.criteria.${criterionIndex}.subCriteria.${subIndex}.weight.number`
  );

  const status = subCriterionStatus(sub);
  const effective = effectiveWeight(criterion, sub);

  const nameText = (
    <Box>
      <Text fontSize="sm">{sub.name?.trim() || t("Missing name")}</Text>
      <ItemComments kind={CommentSubjectKind.AwardCriterion} itemRef={sub.id} />
    </Box>
  );

  const weightCell = (
    <Flex align="center" gap="3">
      <FormControl isInvalid={!!weightFieldError} w="auto">
        <WeightInput
          value={sub.weight?.number}
          isDisabled={isLocked}
          isInvalid={!!weightFieldError}
          isQuiet
          ariaLabel={t("Weight")}
          onChange={(weight) =>
            onChange({
              ...sub,
              weight: weight == null ? undefined : { number: weight, type: WeightType.Percentage },
            })
          }
        />
        {weightFieldError && (
          <FormErrorMessage fontSize="xs">{formatFieldError(weightFieldError)}</FormErrorMessage>
        )}
      </FormControl>
      {effective != null && (
        <Text fontSize="sm" color="smMuted" whiteSpace="nowrap">
          {"→ "}
          <Text as="span" data-testid="award-effective-weight">{`${formatWeightPercent(effective)} %`}</Text>
          {` ${t("of the total")}`}
        </Text>
      )}
    </Flex>
  );

  const statusChip = (
    <AwardStatusChip
      testId="award-sub-criterion-status"
      tone={status === "complete" ? "success" : "warning"}
      label={t(STATUS_LABEL_KEY[status])}
    />
  );

  const actions = (
    <>
      {onSaveToLibrary && (
        <IconButton
          aria-label={
            (isInLibrary ? t("Already in the requirement library") : t("Save to the requirement library")) ?? ""
          }
          icon={<Icon as={FiBookOpen} />}
          variant="ghost"
          size="xs"
          isDisabled={isLocked || isInLibrary}
          onClick={onSaveToLibrary}
        />
      )}
      <IconButton
        aria-label={t("Delete sub-criterion") ?? "Delete"}
        icon={<DeleteIcon />}
        variant="ghost"
        size="xs"
        isDisabled={isLocked}
        onClick={onDelete}
      />
    </>
  );

  if (stacked) {
    return (
      <Box
        data-testid="award-sub-criterion-row"
        bg="smStatusNeutralBg"
        _hover={{ bg: "smBackgroundSecondary" }}
        pl="10"
        pr="3"
        py="2"
        {...rowOpenProps(onOpen)}
      >
        {nameText}
        <Flex align="center" gap="2" wrap="wrap" mt="1">
          {weightCell}
          {statusChip}
          <Flex ml="auto">{actions}</Flex>
        </Flex>
      </Box>
    );
  }

  // Tinted so the level reads at a glance: indentation and the criterion's bold name are too weak on
  // their own once several criteria each carry a couple of requirements. The row's fields set their own
  // background to transparent, so they inherit this rather than sitting on it as white boxes.
  return (
    <Tr
      data-testid="award-sub-criterion-row"
      bg="smStatusNeutralBg"
      // gray.100 over the row's own gray.50 — the same hover the app's other clickable rows use.
      _hover={{ bg: "smBackgroundSecondary" }}
      {...rowOpenProps(onOpen)}
    >
      <Td pl="10">{nameText}</Td>
      <Td>{weightCell}</Td>
      {/* The summary column belongs to the criterion row; a sub-criterion has nothing to summarise. */}
      <Td />
      <Td>{statusChip}</Td>
      <Td textAlign="right" whiteSpace="nowrap">
        {actions}
      </Td>
    </Tr>
  );
};
